import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to enable swipe-back navigation on mobile
 * Swipe from left edge to go back to previous page
 */
export const useSwipeBack = (options = {}) => {
  const { 
    threshold = 80,       // Minimum swipe distance to trigger
    edgeWidth = 40,       // Width of left edge detection zone (larger for iOS)
    enabled = true,
    onSwipeBack = null    // Custom callback, falls back to history.back()
  } = options;
  
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchCurrentX = useRef(0);
  const isSwiping = useRef(false);
  const swipeIndicator = useRef(null);

  const handleSwipeBack = useCallback(() => {
    if (onSwipeBack) {
      onSwipeBack();
    } else {
      window.history.back();
    }
  }, [onSwipeBack]);

  useEffect(() => {
    if (!enabled) return;

    // Create swipe indicator element
    const indicator = document.createElement('div');
    indicator.style.cssText = `
      position: fixed;
      left: 0;
      top: 50%;
      transform: translateY(-50%) translateX(-100%);
      width: 60px;
      height: 60px;
      background: rgba(16, 185, 129, 0.9);
      border-radius: 0 30px 30px 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      transition: transform 0.15s ease-out;
      pointer-events: none;
    `;
    indicator.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><polyline points="15,18 9,12 15,6"/></svg>';
    document.body.appendChild(indicator);
    swipeIndicator.current = indicator;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      // Only start swipe if touch begins near left edge
      if (touch.clientX <= edgeWidth) {
        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
        touchCurrentX.current = touch.clientX;
        isSwiping.current = true;
      }
    };

    const handleTouchMove = (e) => {
      if (!isSwiping.current) return;
      
      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = Math.abs(touch.clientY - touchStartY.current);
      
      touchCurrentX.current = touch.clientX;
      
      // Cancel if vertical movement is significantly greater (user is scrolling)
      if (deltaY > deltaX * 1.5 && deltaY > 30) {
        isSwiping.current = false;
        if (swipeIndicator.current) {
          swipeIndicator.current.style.transform = 'translateY(-50%) translateX(-100%)';
        }
        return;
      }
      
      // Show swipe indicator
      if (deltaX > 10 && swipeIndicator.current) {
        const progress = Math.min(deltaX / threshold, 1);
        const translateX = -100 + (progress * 120);
        swipeIndicator.current.style.transform = `translateY(-50%) translateX(${translateX}%)`;
        swipeIndicator.current.style.opacity = progress;
      }
    };

    const handleTouchEnd = (e) => {
      if (!isSwiping.current) return;
      
      const deltaX = touchCurrentX.current - touchStartX.current;
      
      // Hide indicator
      if (swipeIndicator.current) {
        swipeIndicator.current.style.transform = 'translateY(-50%) translateX(-100%)';
      }
      
      // If swiped right far enough, go back
      if (deltaX > threshold) {
        handleSwipeBack();
      }
      
      isSwiping.current = false;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      if (swipeIndicator.current && swipeIndicator.current.parentNode) {
        swipeIndicator.current.parentNode.removeChild(swipeIndicator.current);
      }
    };
  }, [threshold, edgeWidth, enabled, handleSwipeBack]);
};

export default useSwipeBack;
